import React, { useState } from 'react';
import { useCampaignPlans, useDeleteCampaignPlan } from '@/hooks/useCampaignPlanner';
import { CampaignPlannerWizard } from '@/components/campaign-planner/CampaignPlannerWizard';
import { CampaignPlanCard } from '@/components/campaign-planner/CampaignPlanCard';
import { Button } from '@/components/ui/button';
import { Plus, Megaphone } from 'lucide-react';

const CampaignPlanner = () => {
  const { data: plans, isLoading } = useCampaignPlans();
  const deletePlan = useDeleteCampaignPlan();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState<string | undefined>();

  const handleEdit = (id: string) => {
    setEditingPlanId(id);
    setWizardOpen(true);
  };

  const handleNew = () => {
    setEditingPlanId(undefined);
    setWizardOpen(true);
  };

  const handleWizardClose = () => {
    setWizardOpen(false);
    setEditingPlanId(undefined);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Megaphone className="h-7 w-7 text-teal-500" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Campaign Planner</h1>
            <p className="text-sm text-muted-foreground">AI-powered multi-channel campaign planning</p>
          </div>
        </div>
        <Button onClick={handleNew} className="gap-2">
          <Plus className="h-4 w-4" />
          New Campaign Plan
        </Button>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : plans && plans.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan) => (
            <CampaignPlanCard
              key={plan.id}
              plan={plan}
              onEdit={() => handleEdit(plan.id)}
              onDelete={() => deletePlan.mutate(plan.id)}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Megaphone className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-semibold">No campaign plans yet</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            Create your first AI-powered campaign plan to get started.
          </p>
          <Button onClick={handleNew} className="gap-2">
            <Plus className="h-4 w-4" />
            Create First Plan
          </Button>
        </div>
      )}

      {wizardOpen && (
        <CampaignPlannerWizard
          open={wizardOpen}
          onClose={handleWizardClose}
          editingPlanId={editingPlanId}
        />
      )}
    </div>
  );
};

export default CampaignPlanner;
